import React from 'react';
import { Box, Typography, Button, IconButton, Tooltip as MuiTooltip } from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { getChartColor, trendColors } from '../utils/sampleResponses';
import ChartErrorBoundary from './ChartErrorBoundary';

// Chart color palette
const chartColorPalette = {
  primary: '#22c55e',
  blue: '#3b82f6',
  yellow: '#f59e0b',
  red: '#ef4444',
  green: '#22c55e',
  gray: '#71717a',
  purple: '#8b5cf6',
  cyan: '#06b6d4',
};

// Get trend icon component
const TrendIcon = ({ direction, size = 'small' }) => {
  const iconProps = {
    fontSize: size,
    sx: { color: trendColors[direction] || trendColors.FLAT }
  };

  switch (direction) {
    case 'UP':
      return <TrendingUpIcon {...iconProps} />;
    case 'DOWN':
      return <TrendingDownIcon {...iconProps} />;
    default:
      return <TrendingFlatIcon {...iconProps} />;
  }
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label, colors }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <Box
      sx={{
        bgcolor: colors.surface,
        border: `1px solid ${colors.border}`,
        p: 1.5,
        minWidth: 120,
      }}
    >
      <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      {payload.map((entry, index) => (
        <Typography
          key={index}
          variant="body2"
          sx={{ color: entry.color, fontWeight: 500 }}
        >
          {entry.name}: {entry.value.toLocaleString()}
        </Typography>
      ))}
    </Box>
  );
};

// Bar Chart Renderer
const BarChartRenderer = ({ chart, colors, onActionClick }) => {
  const { data, options = {} } = chart;

  // Transform data for Recharts
  const chartData = data.labels.map((label, index) => {
    const point = { name: label };
    data.datasets.forEach((dataset) => {
      point[dataset.label] = dataset.data[index];
    });
    return point;
  });

  return (
    <Box sx={{ width: '100%', height: 300 }}>
      <ResponsiveContainer>
        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.borderLight} />
          <XAxis
            dataKey="name"
            stroke={colors.textSecondary}
            tick={{ fill: colors.textSecondary, fontSize: 12 }}
            label={options.xAxisLabel ? { value: options.xAxisLabel, position: 'bottom', fill: colors.textSecondary } : undefined}
          />
          <YAxis
            stroke={colors.textSecondary}
            tick={{ fill: colors.textSecondary, fontSize: 12 }}
            label={options.yAxisLabel ? { value: options.yAxisLabel, angle: -90, position: 'insideLeft', fill: colors.textSecondary } : undefined}
          />
          <Tooltip content={<CustomTooltip colors={colors} />} />
          {options.showLegend && <Legend />}
          {data.datasets.map((dataset, index) => (
            <Bar
              key={index}
              dataKey={dataset.label}
              fill={getChartColor(dataset.color)}
              stackId={options.stacked ? 'stack' : undefined}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

// Line Chart Renderer
const LineChartRenderer = ({ chart, colors, onActionClick }) => {
  const { data, options = {} } = chart;

  // Transform data for Recharts
  const chartData = data.labels.map((label, index) => {
    const point = { name: label };
    data.datasets.forEach((dataset) => {
      point[dataset.label] = dataset.data[index];
    });
    return point;
  });

  return (
    <Box sx={{ width: '100%', height: 300 }}>
      <ResponsiveContainer>
        <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.borderLight} />
          <XAxis
            dataKey="name"
            stroke={colors.textSecondary}
            tick={{ fill: colors.textSecondary, fontSize: 12 }}
          />
          <YAxis
            stroke={colors.textSecondary}
            tick={{ fill: colors.textSecondary, fontSize: 12 }}
          />
          <Tooltip content={<CustomTooltip colors={colors} />} />
          {options.showLegend && <Legend />}
          {data.datasets.map((dataset, index) => (
            <Line
              key={index}
              type={options.smooth ? 'monotone' : 'linear'}
              dataKey={dataset.label}
              stroke={getChartColor(dataset.color)}
              strokeWidth={2}
              dot={{ fill: getChartColor(dataset.color), strokeWidth: 2 }}
              activeDot={{ r: 6 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

// Area Chart Renderer
const AreaChartRenderer = ({ chart, colors, onActionClick }) => {
  const { data, options = {} } = chart;

  // Transform data for Recharts
  const chartData = data.labels.map((label, index) => {
    const point = { name: label };
    data.datasets.forEach((dataset) => {
      point[dataset.label] = dataset.data[index];
    });
    return point;
  });

  const fillOpacity = options.fillOpacity || 0.3;

  return (
    <Box sx={{ width: '100%', height: 300 }}>
      <ResponsiveContainer>
        <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.borderLight} />
          <XAxis
            dataKey="name"
            stroke={colors.textSecondary}
            tick={{ fill: colors.textSecondary, fontSize: 12 }}
          />
          <YAxis
            stroke={colors.textSecondary}
            tick={{ fill: colors.textSecondary, fontSize: 12 }}
          />
          <Tooltip content={<CustomTooltip colors={colors} />} />
          {options.showLegend && <Legend />}
          {data.datasets.map((dataset, index) => (
            <Area
              key={index}
              type="monotone"
              dataKey={dataset.label}
              stroke={getChartColor(dataset.color)}
              fill={getChartColor(dataset.color)}
              fillOpacity={fillOpacity}
              strokeWidth={2}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

// Pie/Donut Chart Renderer
const PieChartRenderer = ({ chart, colors, onActionClick }) => {
  const { data, options = {}, chartType } = chart;
  const isDonut = chartType === 'DONUT';

  // Transform segments data
  const chartData = data.segments.map((segment) => ({
    name: segment.label,
    value: segment.value,
    color: getChartColor(segment.color),
  }));

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  // Custom label renderer for percentages
  const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value, name }) => {
    if (!options.showPercentages) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const percent = ((value / total) * 100).toFixed(0);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
      >
        {percent}%
      </text>
    );
  };

  return (
    <Box sx={{ width: '100%', height: 300, position: 'relative' }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={isDonut ? 60 : 0}
            outerRadius={100}
            paddingAngle={2}
            dataKey="value"
            label={renderLabel}
            labelLine={false}
            onClick={(data) => {
              if (onActionClick && chart.actions) {
                const drillDownAction = chart.actions.find(a => a.actionId === 'DRILL_DOWN');
                if (drillDownAction) {
                  const sentence = drillDownAction.sentence.replace('{{clicked_segment}}', data.name);
                  onActionClick(sentence);
                }
              }
            }}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color}
                style={{ cursor: chart.actions ? 'pointer' : 'default' }}
              />
            ))}
          </Pie>
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload || !payload.length) return null;
              const data = payload[0].payload;
              const percent = ((data.value / total) * 100).toFixed(1);
              return (
                <Box sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}`, p: 1.5 }}>
                  <Typography variant="body2" sx={{ color: data.color, fontWeight: 500 }}>
                    {data.name}: {data.value.toLocaleString()} ({percent}%)
                  </Typography>
                </Box>
              );
            }}
          />
          {options.showLegend && <Legend />}
        </PieChart>
      </ResponsiveContainer>

      {/* Center label for donut */}
      {isDonut && options.centerLabel && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          <Typography variant="h4" sx={{ color: colors.textPrimary, fontWeight: 700, lineHeight: 1 }}>
            {options.centerLabel}
          </Typography>
          {options.centerSubLabel && (
            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
              {options.centerSubLabel}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

// KPI Card Renderer
const KpiCardRenderer = ({ chart, colors }) => {
  const { data, options = {} } = chart;
  const { value, unit, trend, sparkline } = data || {};
  const { thresholds } = options;

  // Determine status color based on thresholds
  let statusColor = chartColorPalette.green;
  if (thresholds) {
    if (value < thresholds.critical) {
      statusColor = chartColorPalette.red;
    } else if (value < thresholds.warning) {
      statusColor = chartColorPalette.yellow;
    } else if (value >= thresholds.good) {
      statusColor = chartColorPalette.green;
    }
  }

  // Sparkline data
  const sparklineData = sparkline?.map((val, idx) => ({ value: val, index: idx })) || [];

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderLeft: `4px solid ${statusColor}`,
      }}
    >
      <Typography variant="subtitle2" sx={{ color: colors.textSecondary, mb: 1 }}>
        {chart.title}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
        <Typography variant="h3" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
        {unit && (
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            {unit}
          </Typography>
        )}
      </Box>

      {trend && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <TrendIcon direction={trend.direction} />
          <Typography
            variant="body2"
            sx={{ color: trendColors[trend.direction], fontWeight: 500 }}
          >
            {trend.percentage}%
          </Typography>
          {trend.comparison && (
            <Typography variant="caption" sx={{ color: colors.textMuted }}>
              {trend.comparison}
            </Typography>
          )}
        </Box>
      )}

      {sparklineData.length > 0 && (
        <Box sx={{ width: '100%', height: 40, mt: 1 }}>
          <ResponsiveContainer>
            <AreaChart data={sparklineData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <Area
                type="monotone"
                dataKey="value"
                stroke={statusColor}
                fill={statusColor}
                fillOpacity={0.2}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};

// Multi KPI Renderer
const MultiKpiRenderer = ({ chart, colors, onActionClick }) => {
  const { data, options = {} } = chart;
  const { kpis = [] } = data || {};
  const columns = options.columns || 4;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: `repeat(${Math.min(columns, 2)}, 1fr)`,
          md: `repeat(${columns}, 1fr)`,
        },
        gap: 2,
      }}
    >
      {kpis.map((kpi, index) => (
        <Box
          key={index}
          sx={{
            p: 2,
            bgcolor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderTop: `3px solid ${getChartColor(kpi.color)}`,
          }}
        >
          <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block', mb: 0.5 }}>
            {kpi.label}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
              {typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}
            </Typography>
            {kpi.unit && (
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                {kpi.unit}
              </Typography>
            )}
          </Box>

          {kpi.trend && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <TrendIcon direction={kpi.trend.direction} size="inherit" />
              <Typography
                variant="caption"
                sx={{ color: trendColors[kpi.trend.direction], fontWeight: 500 }}
              >
                {kpi.trend.percentage}%
              </Typography>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

// Chart Actions Renderer
const ChartActions = ({ actions, onActionClick, colors }) => {
  if (!actions || actions.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
      {actions.map((action, index) => (
        <Button
          key={index}
          variant="outlined"
          size="small"
          onClick={() => onActionClick(action.sentence)}
          sx={{
            borderColor: '#22c55e',
            color: '#22c55e',
            '&:hover': {
              borderColor: '#16a34a',
              bgcolor: 'rgba(34, 197, 94, 0.08)',
            },
          }}
        >
          {action.label}
        </Button>
      ))}
    </Box>
  );
};

// Main Chart Renderer Component
const ChartRenderer = ({ chart, colors, onActionClick, onPinToBoard, sourceQuery }) => {
  if (!chart) return null;

  const { chartType, title, description } = chart;

  const renderChart = () => {
    switch (chartType) {
      case 'BAR':
        return <BarChartRenderer chart={chart} colors={colors} onActionClick={onActionClick} />;
      case 'LINE':
        return <LineChartRenderer chart={chart} colors={colors} onActionClick={onActionClick} />;
      case 'AREA':
        return <AreaChartRenderer chart={chart} colors={colors} onActionClick={onActionClick} />;
      case 'PIE':
      case 'DONUT':
        return <PieChartRenderer chart={chart} colors={colors} onActionClick={onActionClick} />;
      case 'KPI_CARD':
        return <KpiCardRenderer chart={chart} colors={colors} />;
      case 'MULTI_KPI':
        return <MultiKpiRenderer chart={chart} colors={colors} onActionClick={onActionClick} />;
      default:
        return (
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            Unsupported chart type: {chartType}
          </Typography>
        );
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Chart header with title and pin button */}
      {(title || onPinToBoard) && chartType !== 'KPI_CARD' && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          {title && (
            <Typography variant="subtitle1" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
              {title}
            </Typography>
          )}
          {onPinToBoard && (
            <MuiTooltip title="Pin to Dashboard" arrow>
              <IconButton
                size="small"
                onClick={() => onPinToBoard('CHART', { chart }, sourceQuery)}
                sx={{
                  color: colors.textSecondary,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 0,
                  p: 0.5,
                  '&:hover': {
                    color: '#22c55e',
                    borderColor: '#22c55e',
                    bgcolor: 'rgba(34, 197, 94, 0.08)',
                  },
                }}
              >
                <PushPinIcon fontSize="small" />
              </IconButton>
            </MuiTooltip>
          )}
        </Box>
      )}
      {description && (
        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
          {description}
        </Typography>
      )}

      {/* Chart content */}
      <ChartErrorBoundary colors={colors}>
        {renderChart()}
      </ChartErrorBoundary>

      {/* Chart actions */}
      <ChartActions actions={chart.actions} onActionClick={onActionClick} colors={colors} />
    </Box>
  );
};

export default ChartRenderer;
